import React, { useEffect, useRef, useState } from 'react';
import { Box, Container, Stack, Typography } from '@mui/material';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import BreadCrumbs from 'components/layouts/BreadCrumbs';
import FilledActionButton from 'components/FilledActionButton';
import UIContainer from 'components/ui/UIContainer';
import WithLoader from 'components/WithLoader';
import { vocabularyTestActions } from 'pages/VocabularyTest/store/vocabularyTest.slice';
import { fmt } from 'pages/VocabularyTest/cefr';
import { buildResultCategories } from 'pages/VocabularyTest/resultFormat';
import TestResultHero from 'pages/VocabularyTest/components/TestResultHero';
import TestResultCategoryStats from 'pages/VocabularyTest/components/TestResultCategoryStats';
import TestResultWordsTable from 'pages/VocabularyTest/components/TestResultWordsTable';
import TestResultAddedModal from 'pages/VocabularyTest/components/TestResultAddedModal';
import { pagePaths } from 'routes/paths';

const VocabularyTestResult: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { vocabularyTestResultId } = useParams();
  const requestedHistoryRef = useRef(false);
  const [modalOpen, setModalOpen] = useState(false);
  const {
    completedTestResult,
    addLearnedWordsLoading,
    learnedWordsAdded,
    vocabularyTestResult,
  } = useAppSelector((store) => store.vocabularyTest);

  // Route param is the test's public UUID (string) — compare as-is, no
  // numeric coercion.
  const resultId = vocabularyTestResultId ?? '';
  const result =
    completedTestResult?.id === resultId
      ? completedTestResult
      : vocabularyTestResult.data?.id === resultId
      ? vocabularyTestResult.data
      : null;

  useEffect(() => {
    if (!result && !requestedHistoryRef.current) {
      requestedHistoryRef.current = true;
      dispatch(vocabularyTestActions.vocabularyTestResultGet(resultId));
    }
  }, [dispatch, result, resultId]);

  useEffect(() => {
    if (learnedWordsAdded) {
      setModalOpen(true);
    }
  }, [learnedWordsAdded]);

  const addAllWordsToVocabulary = () => {
    if (result) {
      dispatch(vocabularyTestActions.addLearnedWordsToVocabulary(result.id));
    }
  };

  if (!result) {
    return (
      <>
        <UIContainer>
          <BreadCrumbs />
        </UIContainer>
        <Container>
          <WithLoader loading={vocabularyTestResult.loading}>
            <Box py={5}>
              <Typography variant={'h1'} sx={{ mb: 3 }}>
                {'Результати тесту'}
              </Typography>
              <Typography variant={'subtitle2'}>
                {'Результат тестування не знайдено.'}
              </Typography>
              <Box mt={3}>
                <FilledActionButton
                  onClick={() => navigate(`/${pagePaths.vocabularyTest}`)}
                >
                  {'Повернутись до тесту'}
                </FilledActionButton>
              </Box>
            </Box>
          </WithLoader>
        </Container>
      </>
    );
  }

  const categories = buildResultCategories(result.learnedWords, result.repeatWords);
  const learnedCount = categories.learned.length;
  const activeCount = categories.active.length;
  const repeatCount = categories.repeat.length;
  const totalAddable = learnedCount + activeCount + repeatCount;

  return (
    <>
      <UIContainer>
        <BreadCrumbs />
      </UIContainer>
      <Container>
        <Box py={5}>
          <Typography variant={'h1'} sx={{ mb: 1 }}>
            {'Результати тесту'}
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 18, mb: 2.75 }}>
            {result.covered}
          </Typography>

          <Stack spacing={2.5}>
            <TestResultHero
              orientedLevel={result.orientedLevel}
              durationOfComplete={result.durationOfComplete}
            />
            <TestResultCategoryStats
              learnedCount={learnedCount}
              repeatCount={repeatCount}
              activeCount={activeCount}
            />
            <Box>
              <Stack direction={'row'} justifyContent={'flex-end'} sx={{ mb: 2 }}>
                <FilledActionButton
                  startIcon={<BookmarkAddIcon />}
                  onClick={addAllWordsToVocabulary}
                  loading={addLearnedWordsLoading}
                  disabled={learnedWordsAdded || totalAddable === 0}
                  sx={{ height: 46, px: 2.75, fontSize: 15 }}
                >
                  {learnedWordsAdded
                    ? '✓ Слова додано у словник'
                    : `Додати всі слова у словник (${fmt(totalAddable)})`}
                </FilledActionButton>
              </Stack>
              <TestResultWordsTable categories={categories} />
            </Box>
          </Stack>
        </Box>
      </Container>

      <TestResultAddedModal
        open={modalOpen}
        wordsCount={totalAddable}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
};

export default VocabularyTestResult;
